import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, Calendar } from 'lucide-react';
import { cropImage } from '@/lib/cropImage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MONTH_FULL = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

// Full seasonal overview — everything the grower has ticked across all months.
export default function SeasonalOverview({ onClose }) {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ planned: 0, planted: 0, total: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/planting-plan`, { withCredentials: true })
      .then((r) => { setItems(r.data.items || []); setCounts(r.data.counts || {}); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const byMonth = {};
  items.forEach((i) => { (byMonth[i.month] = byMonth[i.month] || []).push(i); });
  const months = Object.keys(byMonth).map(Number).sort((a, b) => a - b);

  return (
    <div className="fixed inset-0 z-[90] bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="season-overview">
      <div className="bg-[#FAF6EC] rounded-t-3xl sm:rounded-3xl w-full max-w-lg max-h-[88vh] overflow-y-auto hg-scroll" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-[#FAF6EC] px-6 pt-6 pb-3 flex items-center justify-between z-10">
          <div>
            <h3 className="text-2xl font-bold text-[#1A4331]" style={{ fontFamily: 'Outfit, sans-serif' }}>My Season</h3>
            <p className="text-xs text-[#5C665C]">Everything you’ve planned &amp; planted</p>
          </div>
          <button onClick={onClose} data-testid="overview-close" className="hg-press w-9 h-9 rounded-full flex items-center justify-center" style={{ background: 'rgba(26,67,49,0.06)' }}><X className="w-5 h-5 text-[#1A4331]" /></button>
        </div>
        <div className="px-6 pb-6">
          <div className="flex gap-3 mb-4">
            <div className="flex-1 rounded-2xl border-2 p-4 text-center" style={{ borderColor: 'rgba(107,159,74,0.35)' }}>
              <div className="text-3xl font-bold" style={{ color: '#6B9F4A', fontFamily: 'Outfit, sans-serif' }}>{counts.planted || 0}</div>
              <div className="text-[11px] font-semibold uppercase tracking-wider text-[#7A6B57]">Planted</div>
            </div>
            <div className="flex-1 rounded-2xl border-2 p-4 text-center" style={{ borderColor: 'rgba(201,168,91,0.4)' }}>
              <div className="text-3xl font-bold" style={{ color: '#C9A85B', fontFamily: 'Outfit, sans-serif' }}>{counts.planned || 0}</div>
              <div className="text-[11px] font-semibold uppercase tracking-wider text-[#7A6B57]">Planned</div>
            </div>
          </div>
          {loading ? (
            <p className="text-center text-[#5C665C] py-8">Loading…</p>
          ) : months.length === 0 ? (
            <div className="text-center py-10 text-[#5C665C]" data-testid="overview-empty">
              <Calendar className="w-8 h-8 mx-auto mb-2 opacity-60" />
              Nothing ticked yet. Mark crops as Planned or Planted.
            </div>
          ) : months.map((m) => (
            <div key={m} className="mb-5">
              <p className="text-xs font-bold uppercase tracking-wider text-[#7A6B57] mb-2">{MONTH_FULL[m - 1]}</p>
              <div className="space-y-2">
                {byMonth[m].map((i) => (
                  <div key={`${i.crop_id}-${m}`} data-testid={`overview-row-${i.crop_id}-${m}`} className="flex items-center gap-3 bg-white rounded-xl p-2.5 shadow-sm">
                    <img src={cropImage(i.crop_name, i.category)} alt={i.crop_name} className="w-10 h-10 rounded-lg object-cover" style={{ background: '#D9E1CB' }} />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-[#1A4331] truncate">{i.crop_name}</p>
                      {i.in_planner && <p className="text-[11px] text-[#4E7A33] font-medium">In your planner</p>}
                    </div>
                    <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-full text-white" style={{ background: i.status === 'planted' ? '#6B9F4A' : '#C9A85B' }}>
                      {i.status === 'planted' ? 'Planted' : 'Planned'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
